<script setup lang="ts">
import { computed, onUnmounted, ref, watchEffect } from "vue";
import EmptyStreamImage from "@/assets/Images/EmptyStreamImage.png";
import { useSpeckleStore } from "@/stores/speckle/speckle.store";

const BASE_URL = import.meta.env.VITE_SPECKLE_URL;
async function speckleRequest(url: string) {
  const speckle = useSpeckleStore();
  const result = await fetch(url, {
    headers: { Authorization: `Bearer ${speckle.getToken()}` },
  });
  // The backend can reject a preview (auth/permission/server error). fetch() does not throw on a
  // non-2xx status, so without this guard we'd wrap the error body in a blob URL and render a
  // broken image instead of falling back. Treat any non-ok response as a failed preview.
  if (!result.ok) {
    throw new Error(`Speckle preview request failed with status ${result.status}`);
  }
  return result.blob();
}
async function getPreview(streamId: string) {
  return speckleRequest(`${BASE_URL}/preview/${streamId}`);
}
async function getPreviewAll(streamId: string) {
  return speckleRequest(`${BASE_URL}/preview/${streamId}/all`);
}

const props = withDefaults(
  defineProps<{
    streamId?: string | null;
    /** When false, this component will not fetch previews */
    shouldFetch?: boolean;
    /** When false, shows locked overlay and default image */
    hasAccess?: boolean;
    /** Tailwind height class of the preview box */
    height?: string;
  }>(),
  {
    streamId: null,
    shouldFetch: true,
    hasAccess: true,
    height: "h-[250px]",
  }
);

/** Emitted when no preview can be shown — no stream, an empty preview, or the backend rejected it. */
const emit = defineEmits<{ error: [] }>();

const imageBlob = ref<string | null>(null);
const panoramaBlob = ref<string | null>(null);
const positionMagic = ref(0);
const parent = ref<HTMLDivElement | null>(null);
const panorama = ref<HTMLDivElement | null>(null);
const parentWidth = ref(0);
const parentHeight = ref(0);
const hovered = ref(false);
const isLoading = ref(true);

const shouldShowFallback = ref(false);

const revokeObjectUrl = (url: string | null) => {
  if (url) URL.revokeObjectURL(url);
};

const shouldShowMainPreview = computed(() => !!imageBlob.value);
const shouldShowPanoramicPreview = computed(() => hovered.value && !!imageBlob.value && !!panoramaBlob.value);

const latestMouseEvent = ref<{ clientX: number; clientY: number } | null>(null);
const isIntersecting = ref(false);

watchEffect((onCleanup) => {
  if (!parent.value || isIntersecting.value) return;
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        isIntersecting.value = true;
        observer.disconnect();
        getPreviewImage();
      }
    },
    { threshold: 0 }
  );
  observer.observe(parent.value);
  onCleanup(() => observer.disconnect());
});

async function getPreviewImage() {
  // If parent decides we shouldn't fetch (e.g. item is not in view), do nothing.
  if (!props.shouldFetch) {
    isLoading.value = false;
    // If we have no image yet, show fallback.
    if (!imageBlob.value) shouldShowFallback.value = true;
    return;
  }

  shouldShowFallback.value = false;
  isLoading.value = true;

  try {
    if (!props.streamId) {
      shouldShowFallback.value = true;
      isLoading.value = false;
      emit("error");
      return;
    }

    const blob = await getPreview(props.streamId);

    if (!blob || blob.size === 3888) {
      shouldShowFallback.value = true;
      isLoading.value = false;
      emit("error");
      return;
    }

    revokeObjectUrl(imageBlob.value);
    revokeObjectUrl(panoramaBlob.value);
    imageBlob.value = null;
    panoramaBlob.value = null;

    imageBlob.value = URL.createObjectURL(blob);
    if (hovered.value) fetchPanorama();
  } catch (error) {
    console.log("_error", error);
    shouldShowFallback.value = true;
    emit("error");
  } finally {
    isLoading.value = false;
  }
}

// Fetched on first hover, not with the still: the 24-angle strip is roughly ten times the size and
// most cards are only ever scrolled past.
let panoramaRequest: Promise<void> | null = null;

function fetchPanorama() {
  const streamId = props.streamId;
  if (panoramaBlob.value || panoramaRequest || !streamId || !imageBlob.value) return;
  panoramaRequest = getPreviewAll(streamId)
    .then((panoBlob) => {
      if (panoBlob && panoBlob.size > 0 && props.streamId === streamId) {
        panoramaBlob.value = URL.createObjectURL(panoBlob);
      }
    })
    .catch(() => {
      // panorama unavailable — static preview remains
    })
    .finally(() => {
      panoramaRequest = null;
    });
}

const onMouseEnter = () => {
  if (!props.hasAccess) return;
  hovered.value = true;
  fetchPanorama();
};

const setParentDimensions = () => {
  const { width = 0, height = 0 } = parent.value?.getBoundingClientRect() || {};
  parentWidth.value = width;
  parentHeight.value = height;
};

const calculatePanoramaStyle = (e: any) => {
  latestMouseEvent.value = { clientX: e.clientX, clientY: e.clientY };

  if (parentHeight.value === 0) setParentDimensions();

  const rect = panorama.value?.getBoundingClientRect();
  if (!rect) return;

  const x = e.clientX - rect.left;
  const step = rect.width / 24;
  let index = Math.abs(24 - Math.round(x / step));
  if (index >= 24) index = 24 - 1;

  const scaleFactor = parentHeight.value / 400;
  const actualWidth = scaleFactor * 700;
  const widthDiff = (parentWidth.value - actualWidth) * 0.5;

  positionMagic.value = -(actualWidth * (2 * index + 1) - widthDiff);
};

if (typeof ResizeObserver !== "undefined") {
  const resizeObserver = new ResizeObserver(() => {
    setParentDimensions();
    if (latestMouseEvent.value) {
      calculatePanoramaStyle(latestMouseEvent.value);
    }
  });

  watchEffect((onCleanup) => {
    if (parent.value) {
      resizeObserver.observe(parent.value);
      onCleanup(() => resizeObserver.disconnect());
    }
  });
}

onUnmounted(() => {
  revokeObjectUrl(imageBlob.value);
  revokeObjectUrl(panoramaBlob.value);
});
</script>

<template>
  <div
    ref="parent"
    :class="['relative w-full overflow-hidden', height]"
    @mouseenter="onMouseEnter"
    @mouseleave="hovered = false"
    @mousemove="(e: MouseEvent) => hasAccess && calculatePanoramaStyle(e)"
    @touchstart.passive="hasAccess && fetchPanorama()"
    @touchmove="
      (e: TouchEvent) =>
        hasAccess &&
        calculatePanoramaStyle({
          target: e.target,
          clientX: e.touches[0].clientX,
          clientY: e.touches[0].clientY,
        })
    ">
    <Transition
      enter-from-class="opacity-0"
      enter-active-class="transition duration-normal ease-standard"
      leave-to-class="opacity-0"
      leave-active-class="transition duration-normal ease-standard">
      <template v-if="shouldShowMainPreview">
        <div
          v-if="!shouldShowPanoramicPreview"
          class="absolute top-0 left-0 h-full w-full bg-cover bg-center bg-no-repeat"
          :style="{ backgroundImage: `url('${imageBlob}')` }" />
        <div
          v-else
          ref="panorama"
          class="absolute top-0 left-0 h-full w-full bg-cover bg-no-repeat"
          :style="{
            backgroundImage: `url('${panoramaBlob}')`,
            backgroundPosition: `${positionMagic}px center`,
          }" />
      </template>
    </Transition>

    <template v-if="!shouldShowMainPreview">
      <div
        v-if="isLoading && isIntersecting"
        class="absolute inset-0 z-10 flex items-center justify-center overflow-hidden">
        <div
          class="absolute inset-0 bg-contain bg-center bg-no-repeat"
          :style="{ backgroundImage: `url('${EmptyStreamImage}')` }" />

        <div class="absolute inset-0 backdrop-blur-xs" />

        <span class="loader z-10 h-6 w-6 animate-spin rounded-full border-4 border-slate-300 border-t-slate-600"></span>
      </div>

      <div
        v-else
        class="absolute top-0 left-0 h-full w-full bg-contain bg-center bg-no-repeat"
        :style="{
          backgroundImage: `url('${EmptyStreamImage}')`,
        }" />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.loader {
  border-top-color: #4b5563;
}
</style>
